import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage from "@insite/mobius/LazyImage";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MultiVideoPlayer from "@sunSource/Components/MultiVideoPlayer";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

export interface NewListImageVideoStyle {
    imageWrapper?: InjectableCss;
    ImageAndSpecialwrapper?: InjectableCss;
    ContentImg?: InjectableCss;
    playImageOverlay?: InjectableCss;
    defaultImage?: InjectableCss;
}

interface OwnProps {
    videoUrl: string;
    imageUrl: string;
    altText?: string;
    showImage?: boolean;
    showVideo?: boolean;
    videoPlayIconFlag: boolean;
    videoPlayIcon: string;
    extendedStyles?: NewListImageVideoStyle;
    videoHeight?: string;
    isPlayVideoOnclickThumbnail?: boolean;
    attachments?: any;
    newsListpageDefaultViewAttachmentToShowFlag?: boolean;
}

type Props = OwnProps;

export const newListImageVideoStyle: NewListImageVideoStyle = {
    imageWrapper: {
        css: css`
            position: relative;
            width: 100%;
            &.active {
                &::after {
                    content: "";
                    position: absolute;
                    left: 0px;
                    right: 0px;
                    background: rgba(0, 0, 0, 0.25);
                    width: 100%;
                    height: 97%;
                    top: 0;
                    z-index: 0;
                }
            }
        `,
    },
    ImageAndSpecialwrapper: {
        css: css`
            position: relative;
            width: 100%;
            &.active {
                &::after {
                    content: "";
                    position: absolute;
                    left: 0px;
                    right: 0px;
                    background: rgba(0, 0, 0, 0.25);
                    width: 100%;
                    height: 97%;
                    top: 0;
                    z-index: 0;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,

                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    ContentImg: {
        css: css`
            width: 100%;
            height: 100%;
            justify-content: flex-start;
            img {
                border-radius: 0px;
                object-fit: cover;
                aspect-ratio: 3/1.7 !important;
                height: 100%;
                width: 100%;
                max-height: 200px !important;
                border-top-right-radius: 6px;
                border-top-left-radius: 6px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-height: 191px !important;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    defaultImage: {
        css: css`
            width: 100%;
            height: 100%;
            justify-content: flex-start;
            img {
                border-radius: 0px;
                object-fit: contain;
                padding: 20px;
                aspect-ratio: 3/1.7 !important;
                height: 100%;
                width: 100%;
                max-height: 200px !important;
                border-top-right-radius: 6px;
                border-top-left-radius: 6px;
                background: #ececed;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-height: 191px !important;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    playImageOverlay: {
        css: css`
            position: absolute;
            left: 38%;
            top: 0px;
            right: 38%;
            z-index: 1;
            > img {
                width: 100px;
                height: 100px;
            }
        `,
    },
};

interface PdfJsLib {
    GlobalWorkerOptions: {
        workerSrc: string;
    };
    getDocument: (src: string | { url: string }) => {
        promise: Promise<any>;
    };
}

const stripUrlParamsAndHash = (url: string) => url.split("#")[0].split("?")[0];

const getFileExtension = (url: string) => {
    return stripUrlParamsAndHash(url).split(".").pop() ?? "";
};

const isPdfFile = (url: string) => getFileExtension(url).toLowerCase() === "pdf";

const pdfJsLibPath = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.149/pdf.min.mjs";
const pdfJsWorkerPath = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.149/pdf.worker.min.mjs";


const isLikelyPdfAttachment = (url?: string, name?: string) => {
    const fileUrl = url || "";
    const fileName = name || "";

    if (!fileUrl) {
        return false;
    }

    if (isPdfFile(fileUrl)) {
        return true;
    }

    if (fileName.toLowerCase().endsWith(".pdf")) {
        return true;
    }

    return /pdf/i.test(fileUrl);
};

const loadPdfJs = async (): Promise<PdfJsLib> => {
    const typedWindow = window as Window & {
        pdfjsLib?: PdfJsLib;
        __pdfJsLoadingPromise?: Promise<PdfJsLib>;
    };

    if (typedWindow.pdfjsLib) {
        return typedWindow.pdfjsLib;
    }

    if (typedWindow.__pdfJsLoadingPromise) {
        return typedWindow.__pdfJsLoadingPromise;
    }

    typedWindow.__pdfJsLoadingPromise = (async () => {
        const importedModule = await import(/* webpackIgnore: true */ pdfJsLibPath);
        const pdfjsLib = ((importedModule as any).default ?? importedModule) as PdfJsLib;

        if (!pdfjsLib || !pdfjsLib.getDocument) {
            throw new Error("Failed to initialize pdf.js module.");
        }

        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfJsWorkerPath;
        typedWindow.pdfjsLib = pdfjsLib;

        return pdfjsLib;
    })();

    return typedWindow.__pdfJsLoadingPromise;
};

const PdfCanvasPreview = ({ file, title }: { file: string; title?: string }) => {
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const [hasCanvasPreview, setHasCanvasPreview] = React.useState(false);
    const [canvasPreviewFailed, setCanvasPreviewFailed] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;

        const renderPdfPreview = async () => {
            try {
                const pdfjsLib = await loadPdfJs();
                const loadingTask = pdfjsLib.getDocument({ url: stripUrlParamsAndHash(file) });
                const pdf = await loadingTask.promise;
                const firstPage = await pdf.getPage(1);
                const viewport = firstPage.getViewport({ scale: 1.1 });
                const canvas = canvasRef.current;

                if (!canvas || cancelled) {
                    return;
                }

                const context = canvas.getContext("2d");
                if (!context) {
                    setCanvasPreviewFailed(true);
                    return;
                }

                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await firstPage.render({
                    canvasContext: context,
                    viewport,
                }).promise;

                if (!cancelled) {
                    setHasCanvasPreview(true);
                    setCanvasPreviewFailed(false);
                }
            } catch {
                if (!cancelled) {
                    setCanvasPreviewFailed(true);
                }
            }
        };

        renderPdfPreview();

        return () => {
            cancelled = true;
        };
    }, [file]);

    if (canvasPreviewFailed) {
        return (
            <StyledWrapper
                style={{
                    height: "200px",
                    width: "104%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f5f5f5",
                }}
            >
                <a style={{background: "#dbefff",
    padding: "15px",
    borderRadius: "15px"}} href={stripUrlParamsAndHash(file)} target="_blank" rel="noopener noreferrer" aria-label={title || "PDF"}>
                    Open PDF
                </a>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper style={{ height: "200px", width: "104%", position: "relative" }}>
            {!hasCanvasPreview && (
                <StyledWrapper
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f5f5f5",
                    }}
                >
                    Loading preview...
                </StyledWrapper>
            )}
            <canvas
                ref={canvasRef}
                style={{
                    border: "none",
                    overflow: "hidden",
                    width: "96%",
                    display: hasCanvasPreview ? "block" : "none",
                }}
                aria-label={title || ""}
            />
        </StyledWrapper>
    );
};

const NewListImageVideo = ({
    videoUrl,
    imageUrl,
    altText,
    showImage = true,
    showVideo = true,
    videoPlayIconFlag,
    videoPlayIcon,
    extendedStyles,
    videoHeight,
    isPlayVideoOnclickThumbnail = true,
    attachments = [],
    newsListpageDefaultViewAttachmentToShowFlag= true,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(newListImageVideoStyle, extendedStyles));
    const [playVideo, setPlayVideo] = useState(false);
    const [autoplay, setAutoplay] = useState("0");

    useEffect(() => {
        setPlayVideo(showVideo && videoUrl ? true : false);
    }, [showVideo, videoUrl]);

    const onClickThumbnail = () => {
        if (!isPlayVideoOnclickThumbnail) {
            return;
        }
        setPlayVideo(!videoUrl ? false : true);
        setAutoplay(!videoUrl ? "0" : "1");
    };

    const firstAttachmentFile = attachments?.[0]?.fields?.file || "";
    const firstAttachmentName = attachments?.[0]?.fields?.name || "";
    const firstAttachmentIsPdf = isLikelyPdfAttachment(firstAttachmentFile, firstAttachmentName);

    return (
        <StyledWrapper
            {...styles.imageWrapper}
            className={playVideo && !isPlayVideoOnclickThumbnail ? "active" : ""}
            data-test-selector={`newslistImage_wrapper`}
            onClick={onClickThumbnail}
        >
            {showImage && imageUrl && !playVideo && (
                <StyledWrapper
                    className={videoPlayIconFlag && !playVideo ? "active" : ""}
                    {...styles.ImageAndSpecialwrapper}
                >
                    <LazyImage {...styles.ContentImg} src={imageUrl} altText={altText} />
                </StyledWrapper>
            )}

            {((showVideo && videoUrl) || (isPlayVideoOnclickThumbnail && playVideo)) && (
                <MultiVideoPlayer
                    videoUrl={videoUrl}
                    videoHeight={videoHeight}
                    autoplay={autoplay}
                    playVideo={playVideo}
                />
            )}

            {/* ATTACHMENT PREVIEW (fallback) */}
            {!imageUrl && !videoUrl && firstAttachmentFile && firstAttachmentIsPdf && newsListpageDefaultViewAttachmentToShowFlag && (
                <StyledWrapper style={{ width: "100%", height: "100%", overflow: "hidden", position: "relative" }}>
                    <PdfCanvasPreview file={firstAttachmentFile} title={altText} />

                    <StyledWrapper style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "transparent"
                    }}></StyledWrapper>
                </StyledWrapper>
            )}


            {videoPlayIconFlag && !playVideo && <LazyImage src={videoPlayIcon} {...styles.playImageOverlay} />}

            {!imageUrl && !videoUrl && (!firstAttachmentFile || !firstAttachmentIsPdf || !newsListpageDefaultViewAttachmentToShowFlag) && (
                <StyledWrapper {...styles.ImageAndSpecialwrapper}>
                    <LazyImage {...styles.defaultImage} src={"/UserFiles/sunsource-logo_950.png"} altText="SunSource" />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default NewListImageVideo;
